"use client";

import { useState, useEffect } from "react";

interface JoinRequest {
  groupName: string;
  duration: string;
  name: string;
  mobile: string;
  balance: string;
  groupCode: string;
  status?: "pending" | "approved" | "declined";
}

interface Group {
  groupName: string;
  groupCode: string;
}

const safeJson = async (res: Response) => {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Server did not return valid JSON");
  }
};

export default function DailyWealthRotationGroups() {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await fetch("/api/groups");
        const data = await safeJson(res);
        const formatted: Group[] = (data.details || []).map((item: any) => ({
          groupName: item.grouptag ?? "",
          groupCode: item.groupCode ?? "",
        }));
        setGroups(formatted);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch groups");
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!selectedGroupName) {
      setRequests([]);
      return;
    }
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const response = await fetch("/api/pending/daily", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupName: selectedGroupName }),
        });
        if (!response.ok) throw new Error("Failed to fetch requests");
        const json = await response.json();
        const items = Array.isArray(json.details) ? json.details : [];
        const formatted: JoinRequest[] = items.map((item: any) => ({
          name: item.name ?? "Unknown",
          mobile: item.mobile ?? "N/A",
          balance: item.balance ?? "N/A",
          groupCode: item.groupCode ?? "N/A",
          groupName: item.groupName ?? "N/A",
          duration: item.duration ?? "N/A",
          status: "pending",
        }));
        setRequests(formatted);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, [selectedGroupName]);

  if (loading) return <p className="p-6">Loading requests...</p>;
  if (error) return <p className="p-6 text-red-600">Error: {error}</p>;

  const groupNames = Array.from(new Set(groups.map(g => g.groupName))).filter(Boolean);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-xl font-bold text-foreground mb-6">
        Daily Wealth Rotation Groups
      </h2>
      <div className="mb-4">
        <label className="block mb-1 text-sm font-medium">Group Name</label>
        <select
          value={selectedGroupName}
          onChange={e => setSelectedGroupName(e.target.value)}
          className="border px-2 py-1 rounded min-w-[200px]"
        >
          <option value="">Select Group</option>
          {groupNames.map(name => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4">NAME</th>
            <th className="text-left py-3 px-4">MOBILE</th>
            <th className="text-left py-3 px-4">BALANCE</th>
            <th className="text-left py-3 px-4">DURATION</th>
            <th className="text-left py-3 px-4">GROUPCODE</th>
            <th className="text-left py-3 px-4">GROUPNAME</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(req => (
            <tr key={req.mobile}>
              <td className="px-4 py-2 border">{req.name}</td>
              <td className="px-4 py-2 border">{req.mobile}</td>
              <td className="px-4 py-2 border">{req.balance}</td>
              <td className="px-4 py-2 border">{req.duration}</td>
              <td className="px-4 py-2 border">{req.groupCode}</td>
              <td className="px-4 py-2 border">{req.groupName}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
